// Reusable primitives used across all yeni-design screens.
// Ported from yeni-design/src/primitives.jsx — fully typed for TS strict.
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { I, type IconComponent } from './icons';

// ---------- TR locale formatting ----------
interface FmtTRYOpts {
  showSign?: boolean;
  decimals?: number;
  withSymbol?: boolean;
}

export const fmtTRY = (n: number, opts: FmtTRYOpts = {}): string => {
  const { showSign = false, decimals = 2, withSymbol = true } = opts;
  const abs = Math.abs(n);
  const parts = abs.toFixed(decimals).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decPart = decimals > 0 ? ',' + parts[1] : '';
  const sign = n < 0 ? '−' : showSign ? '+' : '';
  return `${sign}${withSymbol ? '₺' : ''}${intPart}${decPart}`;
};

export const fmtNum = (n: number): string => {
  const parts = Math.abs(n).toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '−' : '') + parts.join(',');
};

export const fmtPct = (n: number, decimals = 1): string =>
  (n > 0 ? '+' : n < 0 ? '−' : '') +
  Math.abs(n)
    .toFixed(decimals)
    .replace('.', ',') +
  '%';

// ---------- SectionCard ----------
interface SectionCardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  bodyClass?: string;
  headerClass?: string;
}

export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  className = '',
  bodyClass = 'p-5',
  headerClass = '',
}: SectionCardProps) {
  return (
    <section className={`bg-surface border border-border rounded-lg ${className}`}>
      {(title || actions) && (
        <header
          className={`flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border ${headerClass}`}
        >
          <div className="min-w-0">
            {title && (
              <h2 className="text-[14px] font-semibold text-ink-900 leading-tight tracking-tight">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-[12px] text-ink-500 mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-1.5 shrink-0">{actions}</div>
          )}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

// ---------- PageHeader ----------
interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  meta,
  className = '',
}: PageHeaderProps) {
  return (
    <div
      className={`flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5 ${className}`}
    >
      <div className="min-w-0">
        <h1 className="text-[22px] lg:text-[28px] font-semibold tracking-tight text-ink-900 leading-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-[13px] text-ink-500 mt-1">{subtitle}</p>
        )}
        {meta && (
          <div className="text-[12px] text-ink-500 mt-1.5">{meta}</div>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      )}
    </div>
  );
}

// ---------- StatusPill ----------
type Tone = 'pos' | 'neg' | 'warn' | 'info' | 'neutral';
type PillSize = 'sm' | 'md';

interface StatusPillProps {
  tone?: Tone;
  dot?: boolean;
  children?: ReactNode;
  size?: PillSize;
  className?: string;
}

export function StatusPill({
  tone = 'neutral',
  dot = true,
  children,
  size = 'md',
  className = '',
}: StatusPillProps) {
  const tones: Record<Tone, { bg: string; fg: string; dot: string; bd: string }> = {
    pos: { bg: 'bg-pos-50', fg: 'text-pos-700', dot: 'bg-pos-600', bd: 'border-pos-100' },
    neg: { bg: 'bg-neg-50', fg: 'text-neg-700', dot: 'bg-neg-600', bd: 'border-neg-100' },
    warn: {
      bg: 'bg-warn-50',
      fg: 'text-warn-700',
      dot: 'bg-warn-600',
      bd: 'border-warn-100',
    },
    info: {
      bg: 'bg-brand-50',
      fg: 'text-brand-700',
      dot: 'bg-brand-600',
      bd: 'border-brand-100',
    },
    neutral: {
      bg: 'bg-ink-100',
      fg: 'text-ink-700',
      dot: 'bg-ink-500',
      bd: 'border-ink-200',
    },
  };
  const t = tones[tone];
  const sizes: Record<PillSize, string> = {
    sm: 'text-[11px] px-1.5 py-0.5 gap-1 h-[18px]',
    md: 'text-[12px] px-2 py-0.5 gap-1.5 h-[22px]',
  };
  return (
    <span
      className={`inline-flex items-center rounded-md border ${t.bg} ${t.fg} ${t.bd} ${sizes[size]} font-medium ${className}`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />}
      {children}
    </span>
  );
}

// ---------- Delta indicator ----------
interface DeltaProps {
  value: number;
  decimals?: number;
  className?: string;
  size?: PillSize;
}

export function Delta({ value, decimals = 1, className = '', size = 'md' }: DeltaProps) {
  const tone: 'pos' | 'neg' | 'neutral' =
    value > 0 ? 'pos' : value < 0 ? 'neg' : 'neutral';
  const colors: Record<'pos' | 'neg' | 'neutral', string> = {
    pos: 'text-pos-700 bg-pos-50',
    neg: 'text-neg-700 bg-neg-50',
    neutral: 'text-ink-600 bg-ink-100',
  };
  const sizes: Record<PillSize, string> = {
    sm: 'text-[11px] px-1.5 py-0.5',
    md: 'text-[12px] px-1.5 py-0.5',
  };
  const Arrow: IconComponent | null =
    value > 0 ? I.ArrowUp : value < 0 ? I.ArrowDown : null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md font-medium tnum ${colors[tone]} ${sizes[size]} ${className}`}
    >
      {Arrow && <Arrow size={10} strokeWidth={2} />}
      {fmtPct(value, decimals).replace(/^[+−]/, '')}
    </span>
  );
}

// ---------- KpiCard ----------
type KpiSize = 'sm' | 'md' | 'lg';

interface KpiCardProps {
  label: ReactNode;
  value: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  delta?: number;
  deltaLabel?: string;
  sub?: ReactNode;
  size?: KpiSize;
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  prefix = '₺',
  suffix = '',
  delta,
  // deltaLabel is decorative metadata kept for parity with source; not rendered yet.
  deltaLabel: _deltaLabel = 'dün',
  sub,
  size = 'md',
  loading,
}: KpiCardProps) {
  void _deltaLabel;
  const sizes: Record<KpiSize, { val: string; pad: string; gap: string }> = {
    sm: { val: 'text-[22px]', pad: 'p-4', gap: 'gap-1' },
    md: { val: 'text-[28px]', pad: 'p-5', gap: 'gap-1.5' },
    lg: { val: 'text-[36px]', pad: 'p-5', gap: 'gap-2' },
  };
  const s = sizes[size];
  return (
    <div
      className={`bg-surface border border-border rounded-lg ${s.pad} flex flex-col ${s.gap}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-ink-500 font-medium">
          {label}
        </span>
        {typeof delta === 'number' && !loading && <Delta value={delta} />}
      </div>
      {loading ? (
        <div className="skeleton h-8 w-2/3 rounded mt-1" />
      ) : (
        <div
          className={`mono ${s.val} font-semibold text-ink-900 leading-none mt-1`}
        >
          {prefix && (
            <span className="text-ink-500 font-normal mr-0.5">{prefix}</span>
          )}
          {value}
          {suffix && (
            <span className="text-ink-500 font-normal ml-1 text-[14px]">
              {suffix}
            </span>
          )}
        </div>
      )}
      {sub && !loading && (
        <div className="text-[12px] text-ink-500 mt-0.5">{sub}</div>
      )}
      {loading && <div className="skeleton h-3 w-1/3 rounded mt-1" />}
    </div>
  );
}

// ---------- DateFilterChips ----------
interface DateFilterOption {
  id: string;
  label: ReactNode;
}

interface DateFilterChipsProps {
  value: string;
  onChange?: (id: string) => void;
  options: DateFilterOption[];
  className?: string;
  extraEnd?: ReactNode;
}

export function DateFilterChips({
  value,
  onChange,
  options,
  className = '',
  extraEnd,
}: DateFilterChipsProps) {
  return (
    <div className={`inline-flex items-center bg-ink-100 rounded-md p-0.5 ${className}`}>
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange && onChange(opt.id)}
            className={`focus-ring text-[12px] font-medium px-2.5 h-7 rounded transition-colors ${
              active ? 'seg-active' : 'text-ink-600 hover:text-ink-900'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
      {extraEnd}
    </div>
  );
}

// ---------- SkeletonLoader ----------
interface SkeletonLoaderProps {
  lines?: number;
  lineClass?: string;
  className?: string;
}

export function SkeletonLoader({
  lines = 3,
  lineClass = 'h-3',
  className = '',
}: SkeletonLoaderProps) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`skeleton rounded ${lineClass}`}
          style={{ width: `${75 - i * 8}%` }}
        />
      ))}
    </div>
  );
}

// ---------- EmptyState ----------
interface EmptyStateProps {
  icon?: IconComponent;
  title: ReactNode;
  body?: ReactNode;
  cta?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: IconComp = I.Inbox,
  title,
  body,
  cta,
  className = '',
}: EmptyStateProps) {
  return (
    <div className={`flex flex-col items-center text-center py-10 px-6 ${className}`}>
      <div className="w-10 h-10 rounded-md border border-border-strong text-ink-400 flex items-center justify-center mb-3">
        <IconComp size={18} strokeWidth={1.5} />
      </div>
      <div className="text-[14px] font-semibold text-ink-900">{title}</div>
      {body && (
        <div className="text-[13px] text-ink-500 mt-1 max-w-sm">{body}</div>
      )}
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

// ---------- Button ----------
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconComponent;
  iconRight?: IconComponent;
  type?: 'button' | 'submit' | 'reset';
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon: IconComp,
  iconRight: IconRight,
  children,
  className = '',
  type = 'button',
  ...rest
}: ButtonProps) {
  const variants: Record<ButtonVariant, string> = {
    primary:
      'bg-brand-700 text-white border border-brand-700 hover:bg-brand-600 hover:border-brand-600',
    secondary:
      'bg-white text-ink-900 border border-border hover:bg-ink-50 hover:border-border-strong',
    ghost:
      'bg-transparent text-ink-700 border border-transparent hover:bg-ink-100',
    danger:
      'bg-white text-neg-700 border border-border hover:bg-neg-50 hover:border-neg-100',
  };
  const sizes: Record<ButtonSize, string> = {
    sm: 'h-7  px-2.5 text-[12px] gap-1.5 rounded-md',
    md: 'h-9  px-3   text-[13px] gap-2   rounded-md',
    lg: 'h-11 px-4   text-[14px] gap-2   rounded-md',
  };
  return (
    <button
      type={type}
      className={`focus-ring inline-flex items-center justify-center font-medium transition-colors ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {IconComp && <IconComp size={size === 'lg' ? 16 : 14} />}
      {children && <span className="whitespace-nowrap">{children}</span>}
      {IconRight && <IconRight size={size === 'lg' ? 16 : 14} />}
    </button>
  );
}

// ---------- IconButton ----------
type IconButtonSize = 'sm' | 'md';
type IconButtonVariant = 'ghost' | 'border';

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  icon: IconComponent;
  label: string;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  type?: 'button' | 'submit' | 'reset';
}

export function IconButton({
  icon: IconComp,
  label,
  size = 'md',
  variant = 'ghost',
  className = '',
  type = 'button',
  ...rest
}: IconButtonProps) {
  const sizes: Record<IconButtonSize, string> = { sm: 'w-7 h-7', md: 'w-9 h-9' };
  const variants: Record<IconButtonVariant, string> = {
    ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
    border:
      'text-ink-700 border border-border bg-white hover:bg-ink-50 hover:border-border-strong',
  };
  return (
    <button
      type={type}
      aria-label={label}
      className={`focus-ring inline-flex items-center justify-center rounded-md ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      <IconComp size={size === 'sm' ? 14 : 16} />
    </button>
  );
}

// ---------- Input ----------
type InputSize = 'sm' | 'md' | 'lg';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  icon?: IconComponent;
  suffix?: ReactNode;
  wrapClass?: string;
  size?: InputSize;
}

export function Input({
  icon: IconComp,
  suffix,
  className = '',
  wrapClass = '',
  size = 'md',
  ...rest
}: InputProps) {
  const sizes: Record<InputSize, string> = {
    sm: 'h-8 text-[12px]',
    md: 'h-9 text-[13px]',
    lg: 'h-11 text-[14px]',
  };
  return (
    <div className={`relative ${wrapClass}`}>
      {IconComp && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
          <IconComp size={14} />
        </span>
      )}
      <input
        className={`w-full bg-white border border-border rounded-md px-3 ${IconComp ? 'pl-9' : ''} ${suffix ? 'pr-9' : ''} ${sizes[size]} text-ink-900 placeholder:text-ink-400 transition-shadow outline-none focus:border-brand-600 focus:shadow-[0_0_0_3px_rgba(29,78,216,0.12)] ${className}`}
        {...rest}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400">
          {suffix}
        </span>
      )}
    </div>
  );
}

// ---------- DataTable ----------
type ColAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<R> {
  key: string;
  label: ReactNode;
  align?: ColAlign;
  width?: number | string;
  sortable?: boolean;
  render?: (row: R, rowIndex: number) => ReactNode;
  sticky?: boolean;
  mono?: boolean;
}

export interface DataTableProps<R extends { id?: string | number }> {
  columns: DataTableColumn<R>[];
  rows: R[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (row: R) => void;
  selectedId?: string | number | null;
  altRow?: boolean;
  zebra?: boolean;
  loading?: boolean;
  loadingRows?: number;
  emptyTitle?: ReactNode;
  emptyBody?: ReactNode;
  className?: string;
}

export function DataTable<R extends { id?: string | number }>({
  columns,
  rows,
  sortKey,
  sortDir = 'asc',
  onSort,
  onRowClick,
  selectedId,
  altRow: _altRow = true,
  zebra = false,
  loading = false,
  loadingRows = 6,
  emptyTitle = 'Kayıt bulunamadı',
  emptyBody,
  className = '',
}: DataTableProps<R>) {
  void _altRow;
  const headerCell = (col: DataTableColumn<R>) => {
    const sortable = col.sortable !== false;
    const isSorted = sortKey === col.key;
    return (
      <th
        key={col.key}
        scope="col"
        className={`px-4 h-10 text-[12px] font-semibold text-ink-600 uppercase tracking-wider whitespace-nowrap bg-ink-50 border-b border-border ${
          col.sticky ? 'sticky-left bg-ink-50' : ''
        } ${
          col.align === 'right'
            ? 'text-right'
            : col.align === 'center'
              ? 'text-center'
              : 'text-left'
        }`}
        style={col.width ? { width: col.width } : undefined}
      >
        {sortable ? (
          <button
            type="button"
            onClick={() => onSort && onSort(col.key)}
            className="inline-flex items-center gap-1 hover:text-ink-900"
          >
            <span>{col.label}</span>
            <span className="flex flex-col leading-none text-ink-400">
              <I.ChevronUp
                size={8}
                strokeWidth={2.5}
                className={isSorted && sortDir === 'asc' ? 'text-ink-900' : ''}
              />
              <I.ChevronDown
                size={8}
                strokeWidth={2.5}
                className={isSorted && sortDir === 'desc' ? 'text-ink-900' : ''}
              />
            </span>
          </button>
        ) : (
          col.label
        )}
      </th>
    );
  };

  return (
    <div className={`overflow-x-auto thin-scroll ${className}`}>
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>{columns.map(headerCell)}</tr>
        </thead>
        <tbody className="bg-white">
          {loading ? (
            Array.from({ length: loadingRows }).map((_, i) => (
              <tr
                key={i}
                className={zebra && i % 2 ? 'bg-ink-50/40' : 'bg-white'}
              >
                {columns.map((c, ci) => (
                  <td
                    key={c.key}
                    className={`px-4 h-12 border-b border-border ${c.sticky ? 'sticky-left' : ''}`}
                    style={c.sticky ? { background: 'inherit' } : undefined}
                  >
                    <div
                      className="skeleton h-3 rounded"
                      style={{ width: `${50 + ((i + ci) % 4) * 10}%` }}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center bg-white border-b border-border"
              >
                <EmptyState title={emptyTitle} body={emptyBody} icon={I.Inbox} />
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const sel = selectedId != null && row.id === selectedId;
              return (
                <tr
                  key={row.id ?? i}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={`row-hover cursor-pointer ${sel ? 'row-selected' : ''} ${zebra && i % 2 ? 'bg-ink-50/50' : 'bg-white'}`}
                >
                  {columns.map((col, ci) => {
                    const sticky = col.sticky;
                    return (
                      <td
                        key={col.key}
                        className={`px-4 h-12 text-[13px] text-ink-900 border-b border-border ${
                          col.align === 'right'
                            ? 'text-right'
                            : col.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        } ${col.mono ? 'mono' : ''} ${sticky ? 'sticky-left' : ''} ${
                          ci === 0 && sel
                            ? 'border-l-[3px] border-l-brand-700 -ml-[3px]'
                            : ''
                        }`}
                        style={
                          sticky
                            ? {
                                background: sel
                                  ? '#eff6ff'
                                  : zebra && i % 2
                                    ? '#f8fafc'
                                    : '#fff',
                              }
                            : undefined
                        }
                      >
                        {col.render
                          ? col.render(row, i)
                          : (row as unknown as Record<string, ReactNode>)[col.key]}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
